import { AfterViewChecked, AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { faChevronLeft, faEnvelope, faFileUpload, faPaperPlane, faPhone, faTrash } from '@fortawesome/free-solid-svg-icons';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AlertService } from 'src/app/alert.service';
import { EmailLog } from 'src/app/email-log';
import { EmailViewComponent } from 'src/app/email-view/email-view.component';
import dayjs from 'dayjs';
import { ConversationService } from 'src/app/conversation.service';
import { Media, Message } from '@twilio/conversations';
import { CallService } from 'src/app/call.service';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { TwilioService } from 'src/app/twilio.service';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NgIf, NgFor } from '@angular/common';

@Component({
    selector: 'app-conversation-content',
    templateUrl: './conversation-content.component.html',
    styleUrls: ['./conversation-content.component.css'],
    imports: [NgIf, FaIconComponent, NgFor, ReactiveFormsModule]
})
export class ConversationContentComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy, AfterViewChecked {
  messagingForm: FormGroup;
  isLoading: boolean;
  @Input() conversation: any;
  @Input() currentUserNumber: any = 123456;
  messageData: any[]
  conversationId: string = null;
  @Input() clientNumber: any = null;
  uploadedFile: File;

  public fileObject: any = {
    filename: '',
    size: '',
    type: '',
    file: ''
  }

  @Output() removeConversation = new EventEmitter();
  @ViewChild('scrollMe') scrollMe: ElementRef;

  isMobileDevice: boolean = /Mobi/i.test(window.navigator.userAgent);

  faPaperPlane = faPaperPlane;
  faPhone = faPhone;
  faChevronLeft = faChevronLeft;
  faEnvelope = faEnvelope;
  faFileUpload = faFileUpload;
  faTrash = faTrash;

  twilioDevice: any;
  componentDestroyed$: Subject<boolean> = new Subject()
  private isUserScrollingUp: boolean = false;

  @HostListener('scroll', ['$event'])
  onScroll(event: Event) {
    this.detectScroll();
  }

  @HostListener('wheel', ['$event'])
  onMouseWheel(event: WheelEvent) {
    this.detectMouseWheel(event);
  }

  constructor(
    private fb: FormBuilder,
    private modalService: NgbModal,
    private alertService: AlertService,
    private callService: CallService,
    private twilioService: TwilioService,
    private conversationSerivce: ConversationService
  ) { }

  ngOnInit(): void {
    if (this.conversation) {
      this.conversationId = this.conversation.id
      this.getMessages()
    }
    this.messagingForm = this.fb.group({
      message: [null, Validators.required]
    })

    this.loadDraft();

    this.messagingForm.controls['message'].valueChanges
      .pipe(
        debounceTime(400),
        distinctUntilChanged()
      )
      .subscribe(res=> {
        if(res){
          this.setDraft(res);
        } else {
          localStorage.removeItem(this.conversationId);
        }

      });

    this.conversationSerivce.twilioClient.subscribe(client => {
      client.on('messageAdded', async (msg: Message) => {
        this.getMessages()
      })
    })

    this.callService.twilioDevice
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((data) => {
        this.twilioDevice = data;
      });
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.conversation) {
      if (changes.conversation.currentValue) {
        this.conversation = changes.conversation.currentValue;
        this.conversationId = this.conversation.id;
        this.messagingForm?.reset();
        this.loadDraft();
        this.removeFile();
        this.getMessages()
      }
    }
  }

  async getMessages() {
    this.conversation.chat.getMessages()
      .then(messages => {
        let media = null;
        // Format messageData
        if (messages && messages.items && messages.items.length != 0) {
          return Promise.all(messages.items.map(async message => {
            let formattedMessages = []

            const dateCreated = dayjs(message.dateCreated).format('MMMM DD, YYYY')
            const timeCreated = dayjs(message.dateCreated).format('hh:mm A')
            const categorizedMedia = await message.getMediaByCategories(["media"]);
            const chatName = this.conversation.chat.friendlyName && this.conversation.chat.friendlyName.trim() != "" ?  this.conversation.chat.friendlyName : 'Unknown Contact'
            if (categorizedMedia && categorizedMedia.length != 0) {
              const url = await this.getFileUrl(categorizedMedia[0])
              media = {
                url,
                type: message.media.contentType,
                size: message.media.size
              }
              formattedMessages.push({
                chatName,
                date: dateCreated,
                message: null,
                senderNumber: message.author && message.author == 'system' ? this.currentUserNumber : message.author,
                time: timeCreated,
                type: media.type,
                media
              })
            }

            if (message.body && message.body != '') {
              formattedMessages.push({
                chatName,
                date: dateCreated,
                message: message.body,
                senderNumber: message.author && message.author == 'system' ? this.currentUserNumber : message.author,
                time: timeCreated,
                type: 'message'
              })
            }
            return formattedMessages

          }
          ))
        }
      }).then(messages => {
        const flattenArrMsg = messages.flat()
        this.messageData = this.groupMessagesByDate(flattenArrMsg);
        this.conversation.clientId = this.conversation.chat.uniqueName;
        this.conversation.unreadCount = this.conversation.chat.getUnreadMessagesCount();

        if(flattenArrMsg && flattenArrMsg.length != 0) {
          const msgType = flattenArrMsg[flattenArrMsg.length - 1].type

          let type = msgType.slice(0, msgType.indexOf('/'));;

          this.conversation.lastConversation = msgType != 'message' ? `Attachment: 1 ${type}`: flattenArrMsg[flattenArrMsg.length - 1].message
        } else {
          this.conversation.lastConversation = 'No Messages'
        }
        this.conversation.chat.updateLastReadMessageIndex(this.conversation.chat.lastMessage.index)
        this.scrollToBottom();
      })
  }

  groupMessagesByDate = (messages) => {
    let formattedMsgs = [];

    if (messages && messages.length != 0) {
      messages.forEach(item => {
        const { message, senderNumber, time, type, media } = item
        const currMessage = formattedMsgs.filter(msg => msg.date == item.date);

        if (currMessage && currMessage.length != 0 && currMessage[0].conversations) {
          currMessage[0].conversations.push({
            message, senderNumber, time, type, media
          })
        } else {
          formattedMsgs.push({
            chatName: item.chatName,
            date: item.date,
            conversations: [
              { message, senderNumber, time, type, media }
            ]
          })
        }
      })
    }

    return formattedMsgs;
  }

  ngAfterViewInit() {
    this.scrollToBottom();
  }

  ngAfterViewChecked() {
    if (!this.isUserScrollingUp) {
      this.scrollToBottom();
    }
  }

  private detectMouseWheel(event: WheelEvent) {
    const element = this.scrollMe.nativeElement;

    if (event.deltaY < 0) {
      this.isUserScrollingUp = true;
    } else if (event.deltaY > 0) {
      const atBottom = element.scrollHeight - element.scrollTop === element.clientHeight;
      if (atBottom) {
        this.isUserScrollingUp = false;
      }
    }
  }

  private detectScroll() {
    const element = this.scrollMe.nativeElement;
    const atBottom = element.scrollHeight - element.scrollTop === element.clientHeight;

    if (element.scrollTop < element.scrollHeight - element.clientHeight - 100) {
      this.isUserScrollingUp = true;
    } else if (atBottom) {
      this.isUserScrollingUp = false;
    }
  }

  scrollToBottom() {
    setTimeout(() => {
      const element = this.scrollMe.nativeElement;
      element.scrollTop = element.scrollHeight;
    }, 100);
  }

  unselectConversation() {
    this.removeConversation.emit(null);
  }

  selectMedia(e: any) {
    const uploaded = e.target.files;
    this.fileObject.filename = uploaded[0].name;
    this.fileObject.type = uploaded[0].type;
    this.fileObject.size = uploaded[0].size
    this.uploadedFile = uploaded[0]

    if (uploaded.length !== 0) {
      const reader = new FileReader();
      reader.readAsDataURL(uploaded[0]);
      reader.onload = (_event) => {
        this.fileObject.file = reader.result;
      }
    }
  }

  removeFile() {
    this.fileObject = {
      filename: '',
      size: '',
      type: '',
      file: null
    }
    this.uploadedFile = null
  }

  async sendSMS() {
    try {
      localStorage.removeItem(this.conversationId);
      let sendMediaOptions = {
        contentType: '',
        media: null
      }
      if(this.uploadedFile){
        const mediaBlob = new Blob([this.uploadedFile], { type: this.fileObject.type });
          sendMediaOptions.contentType = this.fileObject.type;
          sendMediaOptions.media = mediaBlob
      }

      if (this.messagingForm.valid) {
        const sendingMsg = await this.conversation.chat.prepareMessage()
        .setBody(this.messagingForm.get('message').value || "")
        .addMedia(sendMediaOptions)
        .build()
        .send();

        if (sendingMsg) {
          this.removeFile();
          this.isLoading = false;
          this.messagingForm.reset()
          this.alertService.showSuccessAlert('Messsage Sent')
          this.getMessages()
        }
      }
    } catch (err) {
      this.alertService.showErrorAlert('Messsage Sending Failed')
    }
  }

  async openInCallDialog() {
    if (this.clientNumber) {
      const phoneNumber = this.clientNumber.toString().replace(/\D/g, '').slice(-10);
      const formattedNumber = Number(phoneNumber)
      const outGoingCall = await this.twilioDevice.connect({
        params: { To: `+1${formattedNumber}` }
      });
      this.twilioService.outgoingConnection = outGoingCall
    } else {
      this.alertService.showErrorAlert('No phone number')
    }
  }

  getFileUrl = async (media: Media): Promise<string> => {
    return await media.getContentTemporaryUrl().then();
  };

  // TODO not yet implemented
  openEmailViewModal(emailLog: EmailLog): void {
    const modalRef = this.modalService.open(EmailViewComponent);
    modalRef.componentInstance.emailLog = emailLog;
  }

  setDraft(message){
    localStorage.setItem(this.conversationId, message);
  }

  loadDraft(){
    if(localStorage.getItem(this.conversationId)){
      this.messagingForm.controls['message'].setValue(localStorage.getItem(this.conversationId));
    }
  }
}


